import type {ChatMessage,CodexNotification,ResponseAnnotation} from '@reados/contracts';

export type SideChatSource={threadId:string}&({lastTurnId:string;beforeTurnId?:never}|{beforeTurnId:string;lastTurnId?:never});
/** Only a direct child sharing the visible source surface can receive selections.
 * A new fork freezes its boundary only after reuse has been ruled out. */
export function sideChatDestination(sourceThreadId:string,visibleThreadIds:string[],parentFor:(id:string)=>string|undefined,freezeSource:()=>SideChatSource):{kind:'existing';threadId:string}|{kind:'new';source:SideChatSource} {
 const children=visibleThreadIds.includes(sourceThreadId)?visibleThreadIds.filter(id=>id!==sourceThreadId&&parentFor(id)===sourceThreadId):[];
 return children.length===1?{kind:'existing',threadId:children[0]!}:{kind:'new',source:freezeSource()};
}
export function validateSideChatSelection(threadId:string,messages:ChatMessage[],annotations:ResponseAnnotation[]):void {
 if(!annotations.length||annotations.some(item=>item.sourceThreadId!==threadId||!messages.some(message=>message.id===item.sourceMessageId&&typeof message.turnId==='string'&&message.turnId&&!message.turnId.startsWith('pending-'))))throw new Error('无法确认所选文字的会话来源，请刷新对话后重试。');
 if(annotations.some(item=>messages.find(message=>message.id===item.sourceMessageId)?.status==='streaming'))throw new Error('请等待所选回复完成后再开始侧聊。');
}
/** Freeze the actual transcript boundary at click time; the API revalidates ownership. */
export function freezeSideChatSource(threadId:string,nativeThreadId:string|undefined,messages:ChatMessage[],events:CodexNotification[],annotations:ResponseAnnotation[],busy:boolean):SideChatSource {
 if(!nativeThreadId)throw new Error('无法确认所选文字的会话来源，请刷新对话后重试。');
 validateSideChatSelection(threadId,messages,annotations);
 let eventTurn:{id:string;active:boolean}|undefined;
 for(const event of [...events].reverse()){
  if(event.params.threadId!==nativeThreadId||!['turn/started','turn/completed','turn/failed','turn/interrupted'].includes(event.method))continue;
  const turn=event.params.turn;
  if(turn&&typeof turn==='object'&&'id' in turn&&typeof turn.id==='string'&&turn.id){eventTurn={id:turn.id,active:event.method==='turn/started'};break;}
 }
 const last=[...messages].reverse().find(message=>typeof message.turnId==='string'&&message.turnId&&!message.turnId.startsWith('pending-'));
 // GET history can be newer than the SSE replay cursor. A replayed event for
 // an earlier known turn must not truncate a newer selected/message turn.
 const timeline=[...new Set(messages.flatMap(message=>typeof message.turnId==='string'&&!message.turnId.startsWith('pending-')?[message.turnId]:[]))];
 if(eventTurn&&timeline.includes(eventTurn.id)&&timeline.indexOf(eventTurn.id)<timeline.length-1)eventTurn=undefined;
 const activeTurnId=eventTurn?.active?eventTurn.id:last?.status==='streaming'?last.turnId as string:undefined;
 if(activeTurnId){
  if(annotations.some(item=>messages.find(message=>message.id===item.sourceMessageId)?.turnId===activeTurnId))throw new Error('请等待所选回复完成后再开始侧聊。');
  return {threadId,beforeTurnId:activeTurnId};
 }
 if(busy)throw new Error('正在确认当前回复的历史边界，请稍后再开始侧聊。');
 if(eventTurn)return {threadId,lastTurnId:eventTurn.id};
 if(!last)throw new Error('无法确认侧聊的历史边界，请刷新对话后重试。');
 return {threadId,lastTurnId:last.turnId as string};
}

const uuidV4=/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** One stable API key belongs to the pending tab, including legacy local IDs. */
export function pendingForkIdempotencyKey(pendingId:string,stored:string|null):string {
 const tabKey=pendingId.startsWith('pending:')?pendingId.slice('pending:'.length):'';
 if(uuidV4.test(tabKey))return tabKey;
 return stored&&uuidV4.test(stored)?stored:crypto.randomUUID();
}

/** A pending tab owns exactly one creation attempt, retained after a failed turn. */
export function pendingThreadIdentity(initial:string|null,onCreated:(id:string)=>void){
 let id=initial,flight:Promise<string>|null=null;
 return {get id(){return id;},ensure(create:()=>Promise<string>):Promise<string>{
  if(id)return Promise.resolve(id);
  if(flight)return flight;
  flight=(async()=>{const next=await create();if(!next)throw new Error('服务端尚未确认会话，请重试。');id=next;onCreated(next);return next;})().finally(()=>{flight=null;});
  return flight;
 }};
}

/** Only this exact local pending namespace is removed; real conversations stay intact. */
export function clearPendingComposer(storage:Pick<Storage,'length'|'key'|'removeItem'>,pendingKey:string){
 const keys:string[]=[];
 for(let index=0;index<storage.length;index++){const key=storage.key(index);if(key&&(key===pendingKey||key.startsWith(`${pendingKey}.`)))keys.push(key);}
 for(const key of keys)storage.removeItem(key);
}
